import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PRODUCTS, CATEGORIES } from '../data/mock';
import { useCatalog } from '../lib/catalog';

const Shop = () => {
  const { products: PRODUCTS, categories: CATEGORIES } = useCatalog();
  const [params, setParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [priceMax, setPriceMax] = useState(1000);

  const category = params.get('category') || '';
  const q = params.get('q') || '';

  const filtered = useMemo(() => {
    let list = [...PRODUCTS];
    if (category) list = list.filter((p) => p.category === category);
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    list = list.filter((p) => p.price <= priceMax);
    if (sortBy === 'price-low') list.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-high') list.sort((a, b) => b.price - a.price);
    if (sortBy === 'discount') list.sort((a, b) => (b.mrp - b.price) / b.mrp - (a.mrp - a.price) / a.mrp);
    return list;
  }, [category, q, priceMax, sortBy, PRODUCTS]);

  const activeCat = CATEGORIES.find((c) => c.id === category);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category, q]);

  const setCategory = (id) => {
    const newParams = new URLSearchParams(params);
    if (id) newParams.set('category', id);
    else newParams.delete('category');
    setParams(newParams);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Breadcrumb & header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="text-xs text-gray-500">
            <Link to="/" className="hover:text-emerald-700">Home</Link>
            <span className="mx-2">/</span>
            <span>Shop</span>
            {activeCat && (
              <>
                <span className="mx-2">/</span>
                <span className="text-emerald-700">{activeCat.name}</span>
              </>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
            <h1 className="text-2xl font-bold text-gray-800">
              {activeCat ? activeCat.name : q ? `Results for "${q}"` : 'All Products'}
            </h1>
            <span className="text-sm text-gray-500">{filtered.length} products</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid md:grid-cols-[240px_1fr] gap-6">
        {/* Sidebar */}
        <aside className={`${showFilters ? 'block' : 'hidden'} md:block`}>
          <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-32">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-800">Categories</h3>
              <button onClick={() => setShowFilters(false)} className="md:hidden">
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="space-y-1 text-sm max-h-72 overflow-y-auto">
              <li>
                <button
                  onClick={() => setCategory('')}
                  className={`w-full text-left px-2 py-1.5 rounded ${!category ? 'bg-emerald-50 text-emerald-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                >
                  All Categories
                </button>
              </li>
              {CATEGORIES.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setCategory(c.id)}
                    className={`w-full text-left px-2 py-1.5 rounded ${category === c.id ? 'bg-emerald-50 text-emerald-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-2">Max Price</h3>
              <input
                type="range"
                min="20"
                max="1000"
                step="10"
                value={priceMax}
                onChange={(e) => setPriceMax(Number(e.target.value))}
                className="w-full accent-emerald-600"
              />
              <div className="text-xs text-gray-600 mt-1">Up to ₹{priceMax}</div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main>
          <div className="flex items-center justify-between mb-4 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-1" /> Filters
            </Button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="ml-auto text-sm border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="discount">Best Discount</option>
            </select>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg p-12 text-center border border-gray-200">
              <p className="text-gray-500">No products found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Shop;
